import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Stack, Typography } from '@mui/material';
import { getLeadReport } from 'API/lead';
import { Routes } from 'enums';
import { Questionnaire } from 'features/lead/lead-management/lead-manage-questionnaires/lead-manage-questionnaires.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import ReportLanguageDialog from '../report-language-dialog';

interface Props {
  questionnaire: Questionnaire;
  isEven: boolean;
  token: string;

  handleOpenConfirmDeleteQuestionnaireDialog(questionnaire: Questionnaire): () => void;

  handleOpenEditQuestionnaireDialog(questionnaire: Questionnaire): () => void;

  handleOpenMoveQuestionnaireDialog(questionnaire: Questionnaire): () => void;
}

const QuestionnaireItem: React.FC<Props> = ({
  isEven,
  questionnaire,
  handleOpenConfirmDeleteQuestionnaireDialog,
  handleOpenEditQuestionnaireDialog,
  handleOpenMoveQuestionnaireDialog,
}) => {
  const { classes, cx } = useStyles({ isEven });
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [reportLanguageDialog, setReportLanguageDialog] = useState<{ open: boolean; language: string; loading: boolean }>({
    open: false,
    language: 'de',
    loading: false,
  });

  const countCompleted = () => {
    if (_.countBy(questionnaire.participants, 'completed').true) {
      return _.countBy(questionnaire.participants, 'completed').true;
    } else {
      return 0;
    }
  };

  /**
   * Report language dialog handlers
   */

  const handleOpenReportLanguageDialog = () => {
    setReportLanguageDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseReportLanguageDialog = () => {
    setReportLanguageDialog({ open: false, language: 'de', loading: false });
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportLanguageDialog((prev) => ({ ...prev, language: event.target.value }));
  };

  const createPDF = async () => {
    setReportLanguageDialog((prev) => ({ ...prev, loading: true }));

    const res = await getLeadReport(questionnaire.id, reportLanguageDialog.language);
    if (!res.error) {
      const newBlob = new Blob([res], { type: 'application/pdf' });
      saveAs(newBlob, `Report-${questionnaire.id}`);

      handleCloseReportLanguageDialog();
    } else {
      setReportLanguageDialog((prev) => ({ ...prev, loading: false }));
      console.log({ error: res?.error, res });
      handleOpenErrorDialog(res?.error, '/lead/reports/:leadTagId', 'get');
    }
  };

  return (
    <Stack className={classes.leadSurvey} direction={'row'} alignItems={'center'} justifyContent={'space-between'} flexWrap={'wrap'}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Link className={classes.link} to={`${Routes.LEAD_CREATION_DETAILS}?id=${questionnaire.id}`} target={'_blank'}>
            <PaiLink text={questionnaire.name} size={'md'} variant={'primary'} underline={'always'} />
          </Link>
          <OpenInNewIcon className={classes.icon} />
        </Stack>

        {!_.isNil(questionnaire.updatedAt) ? (
          <>
            <Typography className={classes.divider}>/</Typography>
            <Typography className={classes.text}>{`geändert am: ${formatDateTime(questionnaire.updatedAt, 'Pp')}`}</Typography>
          </>
        ) : null}
        <Typography className={classes.divider}>/</Typography>
        <Typography className={classes.text}>
          {`Umfragen beantwortet: ${questionnaire.participants.length === 0 ? 0 : countCompleted()} / ${questionnaire.participants.length}`}
        </Typography>
      </Stack>
      <Stack direction={'row'} spacing={2}>
        <PaiLink text={'bearbeiten'} size={'md'} variant={'primary'} underline={'always'} action={handleOpenEditQuestionnaireDialog(questionnaire)} />
        <PaiLink
          text={'verschieben'}
          size={'md'}
          variant={'primary'}
          underline={'always'}
          action={handleOpenMoveQuestionnaireDialog(questionnaire)}
        />
        <PaiLink
          text={'löschen'}
          size={'md'}
          variant={'primary'}
          underline={'always'}
          action={handleOpenConfirmDeleteQuestionnaireDialog(questionnaire)}
        />
        <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} sx={{ minWidth: '72px' }}>
          {!questionnaire.completed ? (
            <Typography className={cx(classes.text, 'warning')}>ausstehend</Typography>
          ) : (
            <div style={{ marginTop: '-4px', marginRight: '-4px', marginBottom: '-4px' }}>
              <PaiButton text={'Ergebnis'} size={'small'} action={handleOpenReportLanguageDialog} />
            </div>
          )}
        </Stack>
      </Stack>

      <ReportLanguageDialog
        open={reportLanguageDialog.open}
        language={reportLanguageDialog.language}
        loading={reportLanguageDialog.loading}
        handleLanguageChange={handleLanguageChange}
        handleClose={handleCloseReportLanguageDialog}
        handleAcceptLanguage={createPDF}
      />
    </Stack>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  leadSurvey: {
    position: 'relative',
    width: '100%',
    padding: '8px 8px 8px 16px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
    minHeight: '37px',
  },
  divider: {
    fontSize: theme.typography.pxToRem(15),
    marginTop: '1.5px',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    marginTop: '1.5px',
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
  link: {
    textDecoration: 'none',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.greys.medium,
  },
}));

export default QuestionnaireItem;
